/**
 * @fileoverview Admin routes component with authentication and role-based guards.
 */

import React, {useCallback, useEffect} from "react";
import {Route, useNavigate} from "react-router-dom";
import {SentryRoutes} from "../utils/sentryRoute";

import {PageAdmin404} from "../pages/page-admin-404";
import {PageAdminDashboard} from "../pages/page-admin-dashboard";
import {PageAdminLogin} from "../pages/page-admin-login";
import {
  PageAdminChangePassword,
  PageAdminProfileEdit,
} from "../pages/page-admin-user-profile";
import {PageAdminFacilitySettings} from "../pages/page-admin-facility-settings";
import {PageAdminAdmissionExperience} from "../pages/page-admin-admission-experience";
import {PageAdminSearch} from "../pages/page-admin-search/PageAdminSearch";
import {PageAdminActivityLog} from "../pages/page-admin-activity-log";
import {PageAdminSSOLoginForm} from "../pages/page-admin-sso-login-form";
/* Users */
import {PageAdminUsers} from "../pages/page-admin-users";
import {
  PageAdminUserCreate,
  PageAdminUserEdit,
} from "../pages/page-admin-user-edit";

/* Residents */
import {PageAdminResidents} from "../pages/page-admin-residents";
import {ResidentProfile} from "../pages/page-admin-residents/atoms/ResidentProfile";
import {PageAdminResidentsCreate} from "../pages/page-admin-residents-forms/page-admin-residents-create/PageAdminResidentsCreate";
import {PageAdminResidentsEdit} from "../pages/page-admin-residents-forms/page-admin-residents-edit/PageAdminResidentsEdit";

/* Organizations */
import {PageAdminOrganizations} from "../pages/page-admin-organizations";
import {PageAdminOrganizationsCreate} from "../pages/page-admin-organizations-create";
import {PageAdminOrganizationsEdit} from "../pages/page-admin-organizations-edit";
import {PageAdminOrganizationImportExportUsers} from "../pages/page-admin-organizations-users";
import {PageAdminOrganizationsBranding} from "../pages/page-admin-organizations-branding";
import {
  PageAdminSsoConfiguration,
  PageAdminSsoConfigurationCreate,
  PageAdminSsoConfigurationEdit,
} from "../pages/page-admin-sso-configuration";
import {PageAdminFacilityCreate} from "../pages/page-admin-facility-create";

import {AdminIsLoggedIn} from "../models/admin-session/AdminIsLoggedIn";
import {AdminIsLoggedOut} from "../models/admin-session/AdminIsLoggedOut";
import {ReportingRouter} from "./ReportingRouter";

import {HubAndSpokeRouter} from "../pages/page-admin-hub-and-spoke";

import {PageAdminContent} from "../pages/page-admin-content";

import {
  AdminIsNotClerkGuard,
  AdminIsNotResideAdminGuard,
  AdminIsOrganizationAdminGuard,
  AdminIsOrganizationItAdminRouteGuard,
  AdminIsOrganizationAdminOnlyGuard,
  AdminIsResideAdminGuard,
  AdminCanManageUsersGuard,
} from "../models/admin-session/AdminHasRole";
import {PageAdminPreflight} from "../pages/page-admin-preflight";
import {FeatureFlagsRouter} from "../pages/page-admin-feature-flags";
import {AdminAppRefresh} from "./AdminAppRefresh";
import {
  AdminApiClientConfiguration,
  RestApiClientConfiguration,
} from "./useApiClientConfiguration";
import {DashboardAdmissionsContextProvider} from "../atoms/dashboard-table/DashboardAdmissionsContext";
import {StonlyConfig} from "../atoms/stonly/StonlyConfig";
import {AutoLogoutProvider} from "../contexts/AutoLogoutContext";
import {AutoLogoutWarningBar} from "../atoms/auto-logout";
import {dispatch} from "../store";
import {useClearAndCancelCache} from "../hooks/useClearAndCancelCache";
import {ErrorBoundary} from "../atoms/error-boundary";
import {SSOLoginRedirectPage} from "../pages/page-admin-sso-login-form/SSOLoginRedirectPage";

/**
 * Guards must be set statically, so the component is not re-mounted, but updated.
 */
const GuardedPageAdminDashboard = AdminIsLoggedIn(PageAdminDashboard);
const GuardedPageAdminFacilitySettings = AdminIsLoggedIn(
  AdminIsNotClerkGuard(PageAdminFacilitySettings),
);
const GuardedPageAdminActivityLog = AdminIsLoggedIn(
  AdminIsNotClerkGuard(PageAdminActivityLog),
);
const GuardedPageAdminUserEdit = AdminIsLoggedIn(PageAdminUserEdit);
const GuardedPageAdminUserCreate = AdminIsLoggedIn(
  AdminCanManageUsersGuard(PageAdminUserCreate),
);
const GuardedPageAdminProfileEdit = AdminIsLoggedIn(PageAdminProfileEdit);
const GuardedPageAdminChangePassword = AdminIsLoggedIn(PageAdminChangePassword);
const GuardedPageAdminUsers = AdminIsLoggedIn(
  AdminIsNotClerkGuard(PageAdminUsers),
);
const GuardedPageAdminAdmissionExperience = AdminIsLoggedIn(
  PageAdminAdmissionExperience,
);
const GuardedPageAdminSearch = AdminIsLoggedIn(PageAdminSearch);
const GuardedPageAdminPreflight = AdminIsLoggedIn(
  AdminIsNotResideAdminGuard(PageAdminPreflight),
);
const GuardedPageAdminOrganizations = AdminIsLoggedIn(
  AdminIsOrganizationAdminGuard(PageAdminOrganizations),
);
const GuardedPageAdminOrganizationsBranding = AdminIsLoggedIn(
  AdminIsOrganizationAdminGuard(PageAdminOrganizationsBranding),
);
const GuardedPageAdminOrganizationsCreate = AdminIsLoggedIn(
  AdminIsResideAdminGuard(PageAdminOrganizationsCreate),
);
const GuardedPageAdminOrganizationsEdit = AdminIsLoggedIn(
  AdminIsOrganizationAdminGuard(PageAdminOrganizationsEdit),
);
const GuardedPageAdminFacilityCreate = AdminIsLoggedIn(
  AdminIsResideAdminGuard(PageAdminFacilityCreate),
);
const GuardedFeatureFlagsRouter = AdminIsLoggedIn(
  AdminIsResideAdminGuard(FeatureFlagsRouter),
);
const GuardedReportingRouter = AdminIsLoggedIn(ReportingRouter);
const GuardedHSRouter = AdminIsLoggedIn(
  AdminIsResideAdminGuard(HubAndSpokeRouter),
);
const GuardedPageAdminContent = AdminIsLoggedIn(
  AdminIsResideAdminGuard(PageAdminContent),
);
const GuardedPageAdminResidents = AdminIsLoggedIn(PageAdminResidents);
const GuardedPageAdminResidentsProfile = AdminIsLoggedIn(ResidentProfile);
const GuardedPageAdminResidentsCreate = AdminIsLoggedIn(
  PageAdminResidentsCreate,
);
const GuardedPageAdminResidentsEdit = AdminIsLoggedIn(PageAdminResidentsEdit);
const GuardedPageAdminOrganizationImportExportUsers = AdminIsLoggedIn(
  AdminCanManageUsersGuard(PageAdminOrganizationImportExportUsers),
);
const GuardedPageAdminSsoConfiguration = AdminIsLoggedIn(
  AdminIsOrganizationAdminOnlyGuard(PageAdminSsoConfiguration),
);
const GuardedPageAdminSsoConfigurationCreate = AdminIsLoggedIn(
  AdminIsOrganizationAdminOnlyGuard(PageAdminSsoConfigurationCreate),
);
const GuardedPageAdminSsoConfigurationEdit = AdminIsLoggedIn(
  AdminIsOrganizationAdminOnlyGuard(PageAdminSsoConfigurationEdit),
);

const AdminLoginPage = AdminIsLoggedOut(() => (
  <>
    <AdminApiClientConfiguration />
    <PageAdminLogin />
  </>
));

const AdminSsoLoginFormPage = AdminIsLoggedOut(() => (
  <>
    <AdminApiClientConfiguration />
    <PageAdminSSOLoginForm />
  </>
));

/**
 * Content component for authenticated admin routes.
 * Note that render is being used, so when matched route is not re-mounted (as component) but updated.
 * This is required for pages which have local state which needs to be persisted on different routes, e.g. user edit form.
 *
 * @returns {JSX.Element} The admin routes content with authentication and auto-logout.
 */
const AdminRoutesContentBase = () => {
  const navigate = useNavigate();
  const {clearAndCancelCache} = useClearAndCancelCache();

  // Listen for cross-tab logout via localStorage changes
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // When admin session token is cleared in another tab
      if (event.key === "persist:root" && event.newValue) {
        try {
          const rootState = JSON.parse(event.newValue);
          const adminSessionStr = rootState.adminSession;
          if (adminSessionStr) {
            const adminSession = JSON.parse(adminSessionStr);
            if (!adminSession.token || adminSession.token === "null") {
              // Admin logged out in another tab, navigate to login
              window.location.href = "/admin";
            }
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const handleLogout = useCallback(async () => {
    clearAndCancelCache();
    await dispatch.adminSession.logout();
    navigate("/admin", {replace: true});
  }, [clearAndCancelCache, navigate]);

  return (
    <ErrorBoundary>
      <AutoLogoutProvider userType="admin" onLogout={handleLogout}>
        <AutoLogoutWarningBar />
        <AdminAppRefresh>
          <AdminApiClientConfiguration />
          <RestApiClientConfiguration />

          <StonlyConfig />
          <SentryRoutes>
            <Route path="dashboard" element={<GuardedPageAdminDashboard />} />
            <Route
              path="facility/:facilityId/facility-settings"
              element={<GuardedPageAdminFacilitySettings />}
            />
            <Route
              path="facility/:facilityId/activity-log"
              element={<GuardedPageAdminActivityLog />}
            />
            <Route path="residents" element={<GuardedPageAdminResidents />} />
            <Route
              path="residents/create"
              element={<GuardedPageAdminResidentsCreate />}
            />
            <Route
              path="residents/edit/:resident_uuid"
              element={<GuardedPageAdminResidentsEdit />}
            />
            <Route
              path="residents/:resident_uuid"
              element={<GuardedPageAdminResidentsProfile />}
            />
            <Route
              path="workflows/:admissionId"
              element={<GuardedPageAdminAdmissionExperience />}
            />
            <Route
              path="workflows/:admissionId/section/:sectionId"
              element={<GuardedPageAdminAdmissionExperience />}
            />
            <Route
              path="user/:userId/profile"
              element={<GuardedPageAdminProfileEdit />}
            />
            <Route
              path="user/:userId/change-password"
              element={<GuardedPageAdminChangePassword />}
            />
            <Route path="users" element={<GuardedPageAdminUsers />} />
            <Route
              path="users/create"
              element={<GuardedPageAdminUserCreate />}
            />
            <Route
              path="users/:userId/edit"
              element={<GuardedPageAdminUserEdit />}
            />
            <Route
              path="facility/:facilityId/workflows"
              element={<GuardedPageAdminPreflight />}
            />
            <Route
              path="facility/:facilityId/workflows/:admissionId"
              element={<GuardedPageAdminPreflight />}
            />
            <Route
              path="organizations"
              element={<GuardedPageAdminOrganizations />}
            />
            <Route path="search" element={<GuardedPageAdminSearch />} />
            <Route
              path="organizations/create"
              element={<GuardedPageAdminOrganizationsCreate />}
            />
            <Route
              path="organizations/:organizationId/edit"
              element={<GuardedPageAdminOrganizationsEdit />}
            />
            <Route
              path="organizations/:organizationId/branding"
              element={<GuardedPageAdminOrganizationsBranding />}
            />
            <Route
              path="organizations/:organizationId/branding/:themeId"
              element={<GuardedPageAdminOrganizationsBranding />}
            />
            <Route path="sso/login" element={<SSOLoginRedirectPage />} />
            <Route
              path="organizations/:organizationId/create-facility"
              element={<GuardedPageAdminFacilityCreate />}
            />
            <Route
              path="organizations/:organizationId/manage-users"
              element={<GuardedPageAdminOrganizationImportExportUsers />}
            />
            <Route
              path="organizations/:organizationId/sso-configuration/create"
              element={<GuardedPageAdminSsoConfigurationCreate />}
            />
            <Route
              path="organizations/:organizationId/sso-configuration/edit"
              element={<GuardedPageAdminSsoConfigurationEdit />}
            />
            <Route
              path="organizations/:organizationId/sso-configuration"
              element={<GuardedPageAdminSsoConfiguration />}
            />
            <Route
              path="feature-flags/*"
              element={<GuardedFeatureFlagsRouter />}
            />
            <Route path="hub-and-spoke/*" element={<GuardedHSRouter />} />
            <Route
              path="reporting/*"
              element={<GuardedReportingRouter basePath="/admin/reporting" />}
            />
            <Route
              path="analytics/*"
              element={<GuardedReportingRouter basePath="/admin/analytics" />}
            />
            <Route
              path="permissions/*"
              element={<GuardedReportingRouter basePath="/admin/permissions" />}
            />
            <Route path="content" element={<GuardedPageAdminContent />} />
            <Route path="*" element={<PageAdmin404 />} />
          </SentryRoutes>
        </AdminAppRefresh>
      </AutoLogoutProvider>
    </ErrorBoundary>
  );
};

/**
 * Guarded version of AdminRoutesContent that restricts ORGANIZATION_IT_ADMIN to only access the SSO configuration route.
 */
const AdminRoutesContent = AdminIsOrganizationItAdminRouteGuard(
  AdminRoutesContentBase,
);

/**
 * Main admin routes component that handles both login and authenticated routes.
 * Provides context for dashboard admissions and routes between login and admin content.
 *
 * @returns {JSX.Element} The admin routes component.
 */
export const AdminRoutes = () => (
  <DashboardAdmissionsContextProvider>
    <SentryRoutes>
      <Route index element={<AdminLoginPage />} />
      <Route path="sso-login-form" element={<AdminSsoLoginFormPage />} />
      <Route path="*" element={<AdminRoutesContent />} />
    </SentryRoutes>
  </DashboardAdmissionsContextProvider>
);
